import React from 'react';
import { VolumeMetricKey } from '../types/StockData';

interface VolumeMetricSelectorProps {
  selectedMetric: VolumeMetricKey;
  onMetricChange: (metric: VolumeMetricKey) => void;
}

const metricGroups = {
  'Volume Metrics': [
    { key: 'call_volume' as VolumeMetricKey, label: 'Call Volume', description: 'Total call options volume' },
    { key: 'put_volume' as VolumeMetricKey, label: 'Put Volume', description: 'Total put options volume' },
    { key: 'call_volume_bid_side' as VolumeMetricKey, label: 'Call Volume (Bid)', description: 'Call volume on bid side' },
    { key: 'put_volume_bid_side' as VolumeMetricKey, label: 'Put Volume (Bid)', description: 'Put volume on bid side' },
    { key: 'call_volume_ask_side' as VolumeMetricKey, label: 'Call Volume (Ask)', description: 'Call volume on ask side' },
    { key: 'put_volume_ask_side' as VolumeMetricKey, label: 'Put Volume (Ask)', description: 'Put volume on ask side' },
  ],
  'Premium Metrics': [
    { key: 'call_premium' as VolumeMetricKey, label: 'Call Premium', description: 'Total call options premium' },
    { key: 'put_premium' as VolumeMetricKey, label: 'Put Premium', description: 'Total put options premium' },
    { key: 'net_call_premium' as VolumeMetricKey, label: 'Net Call Premium', description: 'Net call premium flow' },
    { key: 'net_put_premium' as VolumeMetricKey, label: 'Net Put Premium', description: 'Net put premium flow' },
    { key: 'bullish_premium' as VolumeMetricKey, label: 'Bullish Premium', description: 'Premium from bullish trades' },
    { key: 'bearish_premium' as VolumeMetricKey, label: 'Bearish Premium', description: 'Premium from bearish trades' },
  ],
  'Open Interest': [
    { key: 'call_open_interest' as VolumeMetricKey, label: 'Call OI', description: 'Call options open interest' },
    { key: 'put_open_interest' as VolumeMetricKey, label: 'Put OI', description: 'Put options open interest' },
  ],
  'Historical Averages': [
    { key: 'avg_3_day_call_volume' as VolumeMetricKey, label: '3D Avg Call Vol', description: '3-day average call volume' },
    { key: 'avg_3_day_put_volume' as VolumeMetricKey, label: '3D Avg Put Vol', description: '3-day average put volume' },
    { key: 'avg_7_day_call_volume' as VolumeMetricKey, label: '7D Avg Call Vol', description: '7-day average call volume' },
    { key: 'avg_7_day_put_volume' as VolumeMetricKey, label: '7D Avg Put Vol', description: '7-day average put volume' },
    { key: 'avg_30_day_call_volume' as VolumeMetricKey, label: '30D Avg Call Vol', description: '30-day average call volume' },
    { key: 'avg_30_day_put_volume' as VolumeMetricKey, label: '30D Avg Put Vol', description: '30-day average put volume' },
  ],
  'Greek Exposure': [
    { key: 'callDelta' as VolumeMetricKey, label: 'Call Delta Exposure', description: 'Aggregate delta exposure from calls' },
    { key: 'putDelta' as VolumeMetricKey, label: 'Put Delta Exposure', description: 'Aggregate delta exposure from puts' },
    { key: 'callGamma' as VolumeMetricKey, label: 'Call Gamma Exposure', description: 'Aggregate gamma exposure from calls' },
    { key: 'putGamma' as VolumeMetricKey, label: 'Put Gamma Exposure', description: 'Aggregate gamma exposure from puts' },
    { key: 'callCharm' as VolumeMetricKey, label: 'Call Charm Exposure', description: 'Aggregate charm exposure from calls' },
    { key: 'putCharm' as VolumeMetricKey, label: 'Put Charm Exposure', description: 'Aggregate charm exposure from puts' },
    { key: 'callVanna' as VolumeMetricKey, label: 'Call Vanna Exposure', description: 'Aggregate vanna exposure from calls' },
    { key: 'putVanna' as VolumeMetricKey, label: 'Put Vanna Exposure', description: 'Aggregate vanna exposure from puts' },
  ],
  'Combined Exposure': [
    { key: 'netDeltaFlow' as VolumeMetricKey, label: 'Net Delta Exposure', description: 'Call Delta + Put Delta' },
    { key: 'netGammaFlow' as VolumeMetricKey, label: 'Net Gamma Exposure', description: 'Call Gamma + Put Gamma' },
    { key: 'netCharmFlow' as VolumeMetricKey, label: 'Net Charm Exposure', description: 'Call Charm + Put Charm' },
    { key: 'netVannaFlow' as VolumeMetricKey, label: 'Net Vanna Exposure', description: 'Call Vanna + Put Vanna' },
  ],
};

export const VolumeMetricSelector: React.FC<VolumeMetricSelectorProps> = ({
  selectedMetric,
  onMetricChange,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
        Select Metric to Graph
      </h4>
      
      <div className="space-y-4">
        {Object.entries(metricGroups).map(([groupName, metrics]) => (
          <div key={groupName}>
            <h5 className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">
              {groupName}
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {metrics.map((metric) => (
                <button
                  key={metric.key}
                  onClick={() => onMetricChange(metric.key)}
                  className={`
                    p-3 rounded-lg text-left transition-all duration-200 border
                    ${selectedMetric === metric.key
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  title={metric.description}
                >
                  <div className="font-medium text-sm">{metric.label}</div>
                  <div className="text-xs opacity-75 mt-1">{metric.description}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
